import { useFolderData } from "@/data/use-folder-data";
import { smartDate } from "@/lib/date";
import { canWriteArtifact } from "@/lib/grant";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faEllipsisV,
  faMagnifyingGlass,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalFileProviderIcon } from "../display/display-icon/ExternalFileProviderIcon";
import { ExternalFileProviderDisplayName } from "../display/display-name/ExternalFileProviderDisplayName";
import { getFileIconByName } from "../file/FileChip";
import { FileStatus } from "../file/FileStatus";
import { RoleGate } from "../gate/RoleGate";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { DeleteFileDialog } from "./DeleteFileDialog";
import { MoveFilesDialog } from "./MoveFilesDialog";

type FileProps = {
  groupSlug: string;
  groupUuid: string;
  kitUuid: string;
  folderUuid: string;
};

export const FolderFiles: React.FC<FileProps> = ({
  groupSlug,
  groupUuid,
  kitUuid,
  folderUuid,
}) => {
  const [fileToDelete, setFileToDelete] = useState<{
    uuid: string;
    name: string;
  } | null>(null);

  const { folder, deleteFile } = useFolderData(groupUuid, kitUuid, folderUuid);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [moveFilesDialogOpen, setMoveFilesDialogOpen] = useState(false);

  const allFilesSelected = useMemo(() => {
    if (!folder || folder.files.length === 0) return false;
    return folder.files.every((file) => selectedFiles.has(file.uuid));
  }, [folder, selectedFiles]);

  const handleDeleteFile = async (fileUuid: string) => {
    const res = await deleteFile(fileUuid);
    if (res.error) {
      toast.error(`Failed to delete file "${fileUuid}"`);
      return;
    }
    toast.success(`File "${fileUuid}" deleted!`);
  };

  const handleSelectAllFiles = (checked: boolean) => {
    if (checked) {
      setSelectedFiles(new Set(folder?.files.map((file) => file.uuid)));
    } else {
      setSelectedFiles(new Set());
    }
  };

  const toggleSelectedFile = (fileUuid: string) => {
    const newSelectedFiles = new Set(selectedFiles);
    if (newSelectedFiles.has(fileUuid)) {
      newSelectedFiles.delete(fileUuid);
    } else {
      newSelectedFiles.add(fileUuid);
    }
    setSelectedFiles(newSelectedFiles);
  };

  const isExternalFolder = !!folder?.externalProvider;
  const canEdit = !!folder && canWriteArtifact(folder) && !isExternalFolder;

  return (
    <>
      <Table className="table-fixed">
        <TableHeader className={cn({ "bg-accent-5": selectedFiles.size > 0 })}>
          <TableRow className="text-muted-foreground font-family-fancy text-xs font-thin tracking-wide">
            <TableHead className="w-8">
              {canEdit && (
                <div className="flex items-center">
                  <Checkbox
                    checked={allFilesSelected}
                    onCheckedChange={handleSelectAllFiles}
                    aria-label={
                      allFilesSelected
                        ? "Deselect all files"
                        : "Select all files"
                    }
                  />
                </div>
              )}
            </TableHead>
            <TableHead className="w-80">
              {selectedFiles.size === 0 && "Files"}
              {selectedFiles.size > 0 && (
                <div className="flex flex-row items-center gap-2">
                  <div>
                    {selectedFiles.size} file{selectedFiles.size > 1 ? "s" : ""}{" "}
                    selected
                  </div>
                  <Tooltip>
                    <TooltipContent>Move to another folder.</TooltipContent>
                    <TooltipTrigger>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setMoveFilesDialogOpen(true)}
                      >
                        <FontAwesomeIcon icon={faArrowRight} />
                      </Button>
                    </TooltipTrigger>
                  </Tooltip>
                </div>
              )}
            </TableHead>
            <TableHead className="w-25">Modified</TableHead>
            <TableHead className="w-10">Status</TableHead>
            <TableHead className="w-20 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {folder?.files.map((file, index) => {
            const isTombstone = !!file.dateDeleted;
            return (
              <TableRow
                key={index}
                className={cn(isTombstone && "text-muted-foreground")}
              >
                <TableCell>
                  {canEdit ? (
                    selectedFiles.size > 0 ? (
                      <Checkbox
                        checked={selectedFiles.has(file.uuid)}
                        onCheckedChange={() => toggleSelectedFile(file.uuid)}
                        aria-label={
                          selectedFiles.has(file.uuid)
                            ? "Deselect File"
                            : "Select File"
                        }
                      />
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        onClick={() => toggleSelectedFile(file.uuid)}
                        aria-label={
                          selectedFiles.has(file.uuid)
                            ? "Deselect File"
                            : "Select File"
                        }
                      >
                        <FontAwesomeIcon icon={getFileIconByName(file.name)} />
                      </Button>
                    )
                  ) : (
                    <FontAwesomeIcon icon={getFileIconByName(file.name)} />
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-1 items-center gap-2 overflow-hidden">
                    <div
                      className={cn(
                        "flex-1 truncate",
                        isTombstone && "line-through",
                      )}
                    >
                      {file.name}
                    </div>
                    <div className="flex items-center gap-4 overflow-hidden">
                      {file.externalProvider !== null ? (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger>
                            <ExternalFileProviderIcon
                              externalProvider={file.externalProvider}
                            />
                          </TooltipTrigger>
                          <TooltipContent className="flex flex-row items-center gap-2">
                            <div>Source: </div>
                            <ExternalFileProviderDisplayName
                              externalProvider={file.externalProvider}
                            />
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {smartDate(
                    file.dateDeleted ?? file.dateSynced ?? file.date_updated,
                  )}
                </TableCell>
                <TableCell>
                  <FileStatus file={file} />
                </TableCell>
                <TableCell className="flex items-center justify-end gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="More Actions"
                        title="More Actions"
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit && (
                        <DropdownMenuItem
                          onSelect={() =>
                            setFileToDelete({
                              uuid: file.uuid,
                              name: file.name,
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </DropdownMenuItem>
                      )}
                      <RoleGate systemRoles="super">
                        <DropdownMenuItem asChild>
                          <Link
                            to="/w/$groupSlug/k/$kitUuid/folders/$folderUuid/files/$fileUuid/debug"
                            params={{
                              groupSlug,
                              kitUuid,
                              folderUuid,
                              fileUuid: file.uuid,
                            }}
                          >
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                            Debug File
                          </Link>
                        </DropdownMenuItem>
                      </RoleGate>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <MoveFilesDialog
        fromFolderUuid={folderUuid}
        kitUuid={kitUuid}
        selectedFileUuids={selectedFiles}
        open={moveFilesDialogOpen}
        onOpenChange={setMoveFilesDialogOpen}
        groupUuid={groupUuid}
        onMoveFiles={() => setSelectedFiles(new Set())}
      />
      <DeleteFileDialog
        fileName={fileToDelete?.name}
        open={!!fileToDelete}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
        onConfirm={() =>
          fileToDelete?.uuid && handleDeleteFile(fileToDelete.uuid)
        }
      />
    </>
  );
};
